/**
 * Client-side identity management
 * Generates and persists a unique local_user_id in localStorage
 * This ID is stable across sessions on the same device/browser
 */

const LOCAL_USER_ID_KEY = "vocly_local_user_id";

/**
 * Generate a random UUID v4
 */
function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create the local user ID
 * This ID is stored in localStorage and persists across sessions
 * 
 * @returns The stable local user ID for this device/browser
 */
export function getLocalUserId(): string {
  if (typeof window === "undefined") {
    // Server-side rendering - return empty string
    return "";
  }

  try {
    // Try to get existing ID
    const existingId = localStorage.getItem(LOCAL_USER_ID_KEY);
    if (existingId) {
      return existingId;
    }

    // Generate new ID
    const newId = generateUUID();
    localStorage.setItem(LOCAL_USER_ID_KEY, newId);
    console.log("Generated new local user ID:", newId);
    return newId;
  } catch (error) {
    // localStorage might be disabled or in private mode
    console.error("Failed to access localStorage for user ID:", error);
    // Return a session-only ID (won't persist across page reloads)
    return generateUUID();
  }
}

/**
 * Clear the local user ID (for testing/debugging)
 * This will cause a new ID to be generated on next access
 */
export function clearLocalUserId(): void {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.removeItem(LOCAL_USER_ID_KEY);
    console.log("Cleared local user ID");
  } catch (error) {
    console.error("Failed to clear local user ID:", error);
  }
}

/**
 * Check if a local user ID exists
 */
export function hasLocalUserId(): boolean {
  if (typeof window === "undefined") return false;
  
  try {
    return localStorage.getItem(LOCAL_USER_ID_KEY) !== null;
  } catch {
    return false;
  }
}

